import { Injectable, signal } from "@angular/core";
import { FeatureService } from "./core/feature.service";
import { LocalStorageHelper } from "./core/static-classes/local-storage-helper";
import { ConfigurationHelper, IMapDetails } from "./core/static-classes/configuration-helper";
import { MapLayerType, NearbyProjectTypeFilterEnum } from "./core/enums";
import { AccountInfo } from "@azure/msal-browser";
import { environment } from "src/environments/environment";
import { RoleHelper } from "./core/static-classes/role-helper";
import { UserService } from "./core/user.service";
import { AppShellSignalRStore } from "./app-shell-signalr.store";

@Injectable()
export class AppShellStore {
    private readonly _featureFlagsLoading = signal<boolean>(false);
    public readonly featureFlagsLoading = this._featureFlagsLoading.asReadonly();
    private readonly _featureFlagsLoaded = signal<boolean>(false);
    public readonly featureFlagsLoaded = this._featureFlagsLoaded.asReadonly();
    private readonly _environmentName = signal<string>(null);
    public readonly environmentName = this._environmentName.asReadonly();
    private readonly _hasAccess = signal<boolean>(true);
    public readonly hasAccess = this._hasAccess.asReadonly();
    private readonly _featureFlags = signal<PlantDesign.API.IToggleFeatureResponse[]>([]);
    public readonly featureFlags = this._featureFlags.asReadonly();

    public readonly user = signal<PlantDesign.UI.IUser>(null);

    constructor(
        private readonly featureService: FeatureService,
        private readonly userService: UserService,
        private readonly signalRStore: AppShellSignalRStore,
    ) {}

    // User

    public async setUser(account: AccountInfo): Promise<void> {
        await this.getFeatureFlags();
        this.setPersistedProperties();
        this.setHasAccess(account);
        this.setEnvironment(account);
        const user = this.userService.set(account);
        this.handleUser(user);
        this.user.set(user);
    }

    private handleUser(user: PlantDesign.UI.IUser): void {
        const isPopOut = this.isPopOutWindow();
        this.signalRStore.init(user, isPopOut);
    }

    // Persisted user preferences

    private setPersistedProperties(): void {
        this.setSelectedMapType();
        this.setSelectedMapDetails();
        this.setNearbyProjectsFilter();
    }

    private setSelectedMapType(): void {
        const persistedMapLayer = LocalStorageHelper.getItem<MapLayerType>(LocalStorageHelper.SELECTED_MAP_TYPE);
        if (persistedMapLayer) {
            ConfigurationHelper.mapLayerType = persistedMapLayer;
        }
    }

    private setSelectedMapDetails(): void {
        const persistedMapDetails = LocalStorageHelper.getItem<IMapDetails>(LocalStorageHelper.SELECTED_MAP_DETAILS);
        if (persistedMapDetails) {
            ConfigurationHelper.mapDetails = persistedMapDetails;
            // Consolidation of earlier versions of map details in browser local storage
            ConfigurationHelper.mapDetails.labels ??= true;
            ConfigurationHelper.mapDetails.placemarks ??= true;
            LocalStorageHelper.setItem(LocalStorageHelper.SELECTED_MAP_DETAILS, ConfigurationHelper.mapDetails);
        }
    }

    private setNearbyProjectsFilter(): void {
        const persistedNearbyProjectsFilter = LocalStorageHelper.getItem<NearbyProjectTypeFilterEnum>(LocalStorageHelper.NEARBY_PROJECTS_FILTER);
        if (persistedNearbyProjectsFilter !== null) {
            ConfigurationHelper.nearbyProjectTypeFilter = persistedNearbyProjectsFilter;
        }
    }

    // Feature flags
    
    private async getFeatureFlags(): Promise<void> {
        this._featureFlagsLoading.set(true);
        const flags = await this.featureService.initializeFeatureFlags();
        this._featureFlags.set(flags);
        this._featureFlagsLoaded.set(true);
        this._featureFlagsLoading.set(false);
    }

    // Environment name
    
    private setEnvironment(account: AccountInfo): void {
        const env = environment.current.toLowerCase();
        if (env !== 'production') {
            this._environmentName.set(env);
            return;
        }
        const roles = account.idTokenClaims?.roles;
        if (roles.includes(RoleHelper.ROLE_TESTER)) {
            this._environmentName.set('beta');
        }
    }

    // No access
    
    public setHasAccess(account: AccountInfo): void {
        let has = true;
        const roles = account.idTokenClaims?.roles;
        if (!roles) {
            has = false;
        }
        if (!roles.includes(RoleHelper.ROLE_READER)) {
            has = false;
        }
        this._hasAccess.set(has);
    }

    // Is pop-out window

    public isPopOutWindow(): boolean {
        let isPopOut = false;
        const popOuts = ['/inspect/pad-by-pad/', '/inspect/turbine-loads/', '/inspect/turbine-lifetime/', '/monitor', '/export/met-station-report/'];
        const loc = globalThis.window.location.pathname;
        popOuts.forEach(popOut => {
            if (loc.includes(popOut)) {
                isPopOut = true;
            }
        });
        return isPopOut;
    }

    public signOut(): void {
        this.signalRStore.stopSignalRConnection();
    }

}
